import React, { useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EventPerformance = () => {
  const [activeTab, setActiveTab] = useState('popular');

  const popularEvents = [
    { name: 'Tech Conference 2024', value: 45, revenue: 13500, attendees: 450 },
    { name: 'Design Workshop', value: 25, revenue: 7500, attendees: 250 },
    { name: 'Marketing Summit', value: 20, revenue: 6000, attendees: 200 },
    { name: 'Startup Pitch Night', value: 10, revenue: 3000, attendees: 100 }
  ];

  const attendanceData = [
    { event: 'Tech Conf', registered: 450, attended: 423, rate: 94 },
    { event: 'Design WS', registered: 250, attended: 238, rate: 95 },
    { event: 'Marketing', registered: 200, attended: 185, rate: 93 },
    { event: 'Startup', registered: 100, attended: 89, rate: 89 }
  ];

  const COLORS = ['#2563EB', '#93C5FD', '#DBEAFE', '#F1F5F9'];

  const tabs = [
    { id: 'popular', label: 'Popular Events', icon: 'TrendingUp' },
    { id: 'attendance', label: 'Attendance Rates', icon: 'Users' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Event Performance</h3>
          <p className="text-sm text-muted-foreground">Analyze your most successful events</p>
        </div>
        
        <div className="flex bg-muted rounded-lg p-1">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-md transition-smooth ${
                activeTab === tab?.id
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span className="hidden sm:inline">{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>
      {activeTab === 'popular' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pie Chart */}
          <div className="space-y-4">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={popularEvents}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {popularEvents?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => [`${value}%`, 'Share']}
                    contentStyle={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            <div className="space-y-2">
              {popularEvents?.map((event, index) => (
                <div key={index} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-smooth">
                  <div className="flex items-center space-x-3">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: COLORS?.[index % COLORS?.length] }}
                    ></div>
                    <span className="text-sm font-medium text-foreground">{event?.name}</span>
                  </div>
                  <span className="text-sm text-muted-foreground">{event?.value}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Event Details */}
          <div className="space-y-4">
            <h4 className="font-semibold text-foreground">Event Details</h4>
            {popularEvents?.map((event, index) => (
              <div key={index} className="p-4 rounded-lg border border-border hover:shadow-sm transition-smooth">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-medium text-foreground">{event?.name}</h5>
                  <div 
                    className="w-2 h-2 rounded-full" 
                    style={{ backgroundColor: COLORS?.[index % COLORS?.length] }}
                  ></div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Revenue</span>
                    <p className="font-semibold text-success">${event?.revenue?.toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Attendees</span>
                    <p className="font-semibold text-foreground">{event?.attendees}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {activeTab === 'attendance' && (
        <div className="space-y-6">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={attendanceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis 
                  dataKey="event" 
                  stroke="#64748B"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis 
                  stroke="#64748B"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }}
                />
                <Bar dataKey="registered" fill="#93C5FD" name="Registered" radius={[0, 0, 4, 4]} />
                <Bar dataKey="attended" fill="#2563EB" name="Attended" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {attendanceData?.map((event, index) => (
              <div key={index} className="p-4 rounded-lg border border-border">
                <h5 className="font-medium text-foreground mb-2">{event?.event}</h5>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Registered:</span>
                    <span className="font-medium">{event?.registered}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Attended:</span>
                    <span className="font-medium">{event?.attended}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Rate:</span>
                    <span className={`font-semibold ${event?.rate >= 90 ? 'text-success' : 'text-warning'}`}>
                      {event?.rate}%
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="mt-6 pt-4 border-t border-border flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Data updated 5 minutes ago
        </div>
        <Button variant="outline" size="sm">
          <Icon name="Download" size={16} className="mr-2" />
          Export Report
        </Button>
      </div>
    </div>
  );
};

export default EventPerformance;